import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { IonicModule } from '@ionic/angular/lazy';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseComponent } from '@app/core/base/base-component';

export type SelectOption<T = any> = {
  label: string;
  value: T;
  disabled?: boolean;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-form-select',
  templateUrl: './form-select.component.html',
  styleUrls: ['./form-select.component.scss'],
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule],
})
export class FormSelectComponent<T = any> extends BaseComponent implements OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() interface: 'alert' | 'action-sheet' | 'popover' = 'alert';
  @Input() multiple: boolean = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  @Input() options: Array<SelectOption<T>> = [];

  @Input() controlName: string = '';
  @Input() form?: FormGroup;
  @Input() errors: { [key: string]: string } = {};
  // 必須制御を外部から切り替えるためのフラグ
  @Input() required: boolean = false;

  @Output() ionChange = new EventEmitter<any>();

  private readonly defaultControl = new FormControl();

  get control(): FormControl {
    if (!this.form || !this.controlName) return this.defaultControl;
    const control = this.form.get(this.controlName);
    return (control as FormControl) || this.defaultControl;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['required'] ||
      changes['form'] ||
      changes['controlName']
    ) {
      this.syncRequiredValidator();
    }
  }

  /**
   * required入力に応じてValidators.requiredを付与/除去する
   */
  private syncRequiredValidator(): void {
    const control = this.control;
    if (!control) return;

    if (this.required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    // 値の検証を更新（イベントは飛ばさない）
    control.updateValueAndValidity({ emitEvent: false });
  }

  get isInvalid(): boolean {
    const control = this.control;
    if (!control?.invalid) return false;
    return !!(control.touched || control.errors?.['serverError']);
  }

  get errorMessages(): string[] {
    const messages: string[] = [];
    const control = this.control;
    const errs = control?.errors;
    if (errs) {
      if (typeof errs['serverErrorMessage'] === 'string') {
        messages.push(errs['serverErrorMessage']);
      }
      if (this.errors) {
        for (const key in errs) {
          if (key !== 'serverErrorMessage') {
            const msg = this.errors[key];
            if (msg) messages.push(msg);
          }
        }
      }
    }
    return messages;
  }

  get fontSizePx(): number {
    switch (this.size) {
      case 'sm':
        return 14;
      case 'lg':
        return 18;
      case 'md':
      default:
        return 16;
    }
  }
}
